import { useQuery } from '@apollo/react-hooks';
import ContractRejected from 'components/ContractRejected';
import OnlineFormAgreement from 'components/OnlineFormAgreement';
import OnlineFormData from 'components/OnlineFormData';
import OnlineFormNotFound from 'components/OnlineFormNotFound';
import OnlineFormStarter from 'components/OnlineFormStarter/OnlineFormStarter';
import OnlineFormSummary from 'components/OnlineFormSummary';
import OnlineFormTransmitReport from 'components/OnlineFormTransmitReport';
import { FormScene } from 'constants/FormScene';
import { Loading } from 'modules';
import { ContractOnlineFormQuery } from 'queries/ContractOnlineForms/ContractOnlineFormData';
import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import NotFoundScene from 'scenes/NotFound';
import { WebsocketActions, WebsocketContext } from 'services/WebsocketContext';
import {
  changeFormStep,
  selectOnlineForm,
  selectOnlineFormContract,
  selectOnlineFormIsInvalid,
  setClientLogged,
  setInvalidateOnlineForm,
  setManagedMode,
  setMeetingMode,
  setOnlineForm,
} from 'stores/onlineForm/onlineForm';

interface FormWrapperParams {
  formId?: string;
  managed?: string;
  meetingMode?: string;
}

const OnlineForm = () => {
  const dispatch = useDispatch();

  const form = useSelector(selectOnlineForm);
  const contract = useSelector(selectOnlineFormContract);
  const isInvalid = useSelector(selectOnlineFormIsInvalid);

  const wsContext = useContext(WebsocketContext);

  const { formId, managed, meetingMode } = useParams();

  const formDataQuery = useQuery(ContractOnlineFormQuery, {
    variables: { formId: formId?.replace(/[,.]/g, '') },
    fetchPolicy: 'no-cache',
  });

  const loading = formDataQuery?.loading;

  useEffect(() => {
    if (formDataQuery?.data?.contractOnlineForm) {
      dispatch(setOnlineForm(formDataQuery?.data?.contractOnlineForm));
      dispatch(setManagedMode(managed === 'managed'));
      dispatch(setMeetingMode(meetingMode || 'online'));
    }
    // eslint-disable-next-line
  }, [formDataQuery]);

  // dohled nad změnou stránky (kroku)
  useEffect(() => {
    wsContext?.ws?.on(WebsocketActions.STEP_CHANGE, (data: string) => {
      dispatch(changeFormStep(data));
    });
    return () => {
      wsContext?.ws?.off(WebsocketActions.STEP_CHANGE);
    };
  }, [wsContext, dispatch]);

  // dohled nad změnou typu meetingu (Online/Offline)
  useEffect(() => {
    wsContext?.ws?.on(WebsocketActions.MEETING_MODE_CHANGE, (data: string) => {
      dispatch(setMeetingMode(data));
    });
    return () => {
      wsContext?.ws?.off(WebsocketActions.MEETING_MODE_CHANGE);
    };
  }, [wsContext, dispatch]);

  // dohled nad invalidací dat formuláře
  useEffect(() => {
    if (isInvalid) {
      dispatch(setInvalidateOnlineForm(false));
      formDataQuery.refetch();
    }
    // eslint-disable-next-line
  }, [isInvalid]);

  // dohled nad přihlášením klienta
  useEffect(() => {
    wsContext?.ws?.on(WebsocketActions.CLIENT_LOGGED, (data) => {
      dispatch(setClientLogged(data));
    });
    return () => {
      wsContext?.ws?.off(WebsocketActions.CLIENT_LOGGED);
    };
  }, [wsContext, dispatch]);

  // dohled nad požadavkem refreshnout data formuláře
  useEffect(() => {
    wsContext.ws?.on(WebsocketActions.REFRESH_CONTRACT_STATE, () => {
      dispatch(setInvalidateOnlineForm(true));
    });
    return () => {
      wsContext.ws?.off(WebsocketActions.REFRESH_CONTRACT_STATE);
    };
  }, [wsContext, dispatch]);

  if (loading) {
    return <Loading />;
  }

  if (formDataQuery.error) {
    return <NotFoundScene />;
  }

  if (!form) {
    return <OnlineFormNotFound />;
  }

  if (contract?.status?.isstorno) {
    return <ContractRejected />;
  }

  switch (form.status || FormScene.Starter) {
    case FormScene.TransmitReport:
    case 'formReportSigned':
      return <OnlineFormTransmitReport />;

    case FormScene.Summary:
    case 'paymentSaved':
    case 'formSaved':
    case 'formSigned':
    case 'summary':
      return <OnlineFormSummary />;

    case FormScene.Detail:
    case 'presentationSaved':
    case 'questionSaved':
    case 'questionsSaved':
    case 'agreementSaved':
    case 'detailSaved':
      return <OnlineFormData />;

    case FormScene.Agreement:
    case 'meetingStarted':
      return <OnlineFormAgreement />;

    case FormScene.Starter:
    case 'aboutUsConfirm':
    default:
      return <OnlineFormStarter />;
  }
};

export default OnlineForm;
